import {
  ComponentType,
  CSSProperties,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import classNames from 'classnames';
import ReactSelect, {
  components as defaultComponents,
  MenuProps,
  OptionsType,
  OptionTypeBase,
  Props as ReactSelectProps,
  ValueContainerProps,
} from 'react-select';
import Async from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';

import Spinner from '../Spinner';
import IndicatorSeparator from './IndicatorSeparator';

import './Select.scss';

import {
  AnalyticsContext,
  handleAnalyticsEvent,
  TAnalyticsEventData,
} from '@dtci/dash/analytics';

import { SystemColorTypes, TestMetadata } from '../types';

export type SelectProps<T extends OptionTypeBase> = ReactSelectProps<T> & {
  /** @deprecated For testing, use `react-select-event` src: https://github.com/romgain/react-select-event */
  testMetadata?: TestMetadata;
  singleValueOptionColor?: SystemColorTypes;
};

const LoadingIndicator = (): JSX.Element => {
  return <Spinner className="sb-select__loading-indicator" size="small" />;
};

const Select = <
  T extends OptionTypeBase = {
    value: string;
    label: string;
    isDisabled?: boolean;
  },
>({
  className,
  components,
  styles,
  isFetching,
  icon,
  useMenuPortal = false,
  iconContainerClassName,
  isError = false,
  testMetadata,
  flush,
  customNoOptionsMessage,
  creatable,
  async,
  loadOptions,
  selectRef,
  analyticsEventData,
  singleValueOptionColor,
  ...otherProps
}: SelectProps<T> & {
  icon?: ReactNode;
  customNoOptionsMessage?: string;
  iconContainerClassName?: string;
  useMenuPortal?: boolean;
  isError?: boolean;
  flush?: 'left' | 'right' | 'flush';
  creatable?: boolean;
  async?: boolean;
  loadOptions?: (
    inputValue: string,
    callback: (options: OptionsType<T>) => void,
  ) => void | Promise<T[]>;
  selectRef?:
    | MutableRefObject<ReactSelect<T, false> | null>
    | MutableRefObject<AsyncCreatableSelect<T, true> | null>;
  analyticsEventData?: TAnalyticsEventData;
}): JSX.Element | null => {
  const ValueContainer = useMemo<ComponentType<ValueContainerProps<T, false>>>(
    () =>
      icon
        ? ({ children, ...props }) => (
            <defaultComponents.ValueContainer {...props}>
              {!!children && (
                <div
                  className={classNames(
                    'sb-select__icon-container',
                    iconContainerClassName,
                  )}
                >
                  {icon}
                </div>
              )}
              <div>{children}</div>
            </defaultComponents.ValueContainer>
          )
        : defaultComponents.ValueContainer,
    [icon, iconContainerClassName],
  );

  const props: SelectProps<T> = useMemo(() => {
    return {
      isSearchable: async || creatable || false,
      tabSelectsValue: creatable || false,
      menuPortalTarget:
        useMenuPortal && typeof window !== 'undefined' ? document.body : null,
      ...otherProps,
      isLoading: isFetching,
      instanceId: otherProps.instanceId || otherProps.id,
      noOptionsMessage: customNoOptionsMessage
        ? () => customNoOptionsMessage
        : undefined,
      components: {
        LoadingIndicator,
        ValueContainer,
        IndicatorSeparator: (indicatorProps) => (
          <IndicatorSeparator
            {...indicatorProps}
            isClearable={otherProps.isClearable}
          />
        ),
        ...components,
      },
      styles: {
        ...styles,
        menu: (provided: CSSProperties, state: MenuProps<T, false>) => ({
          ...provided,
          position: otherProps.expands ? 'static' : 'absolute',
          zIndex: 20,
          ...(styles?.menu && styles.menu(provided, state)),
        }),
        menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 20 }),
        singleValue: (base: CSSProperties) => ({
          ...base,
          color: `var(--sbt-system-color-${singleValueOptionColor}, var(--sb-select-single-value-color, var(--sbt-color-foreground)))`,
        }),
      },
      classNamePrefix: 'sb-select',
      className: classNames(
        'sb-select',
        {
          'sb-select_error': isError,
          'sb-select__flush-right': flush === 'right',
          'sb-select__flush-left': flush === 'left',
          'sb-select__flush': flush === 'flush',
        },
        className,
      ),
    };
  }, [
    async,
    creatable,
    useMenuPortal,
    otherProps,
    isFetching,
    ValueContainer,
    customNoOptionsMessage,
    components,
    styles,
    isError,
    flush,
    className,
    singleValueOptionColor,
  ]);

  const analyticsContext = useContext(AnalyticsContext);

  const handleOnChange = useCallback(
    (value, action): void => {
      props?.onChange?.(value, action);

      if (analyticsEventData) {
        handleAnalyticsEvent({
          analyticsContext,
          analyticsEventData,
          componentType: 'Select',
          actionName: 'onChange',
          label:
            otherProps?.['aria-label'] ||
            otherProps?.name ||
            className ||
            testMetadata?.['data-testid'] ||
            '',
          value: [value],
        });
      }
    },
    [
      analyticsContext,
      analyticsEventData,
      className,
      otherProps,
      props,
      testMetadata,
    ],
  );

  if (creatable) {
    if (async && loadOptions) {
      return (
        <AsyncCreatableSelect<T, false>
          {...props}
          ref={
            selectRef as MutableRefObject<AsyncCreatableSelect<T, false> | null>
          }
          loadOptions={loadOptions}
          onChange={handleOnChange}
        />
      );
    } else {
      return (
        <CreatableSelect<T, false>
          {...props}
          ref={
            selectRef as MutableRefObject<AsyncCreatableSelect<T, false> | null>
          }
          onChange={handleOnChange}
        />
      );
    }
  } else {
    if (async && loadOptions) {
      return (
        <Async<T, false>
          {...props}
          ref={
            selectRef as MutableRefObject<AsyncCreatableSelect<T, false> | null>
          }
          loadOptions={loadOptions}
          onChange={handleOnChange}
        />
      );
    } else {
      return (
        <ReactSelect<T, false>
          {...props}
          ref={selectRef as MutableRefObject<ReactSelect<T, false> | null>}
          onChange={handleOnChange}
        />
      );
    }
  }
};

export default Select;
